import React, { useState } from "react";
import ConsultationForm from "./Consultation"; // adjust path if your pages folder is elsewhere
import LabAndPathology1 from "../assets/images/Lab/LabAndPathology-1.png";
import LabAndPathology2 from "../assets/images/Lab/LabAndPathology-2.png";
import LabAndPathology3 from "../assets/images/Lab/LabAndPathology-3.png";
import LabAndPathology4 from "../assets/images/Lab/LabAndPathology-4.png";
import LabAndPathology5 from "../assets/images/Lab/LabAndPathology-5.png";
import LabAndPathology6 from "../assets/images/Lab/LabAndPathology-6.png";
import LabAndPathology7 from "../assets/images/Lab/LabAndPathology-7.png";
import LabAndPathology8 from "../assets/images/Lab/LabAndPathology-8.png";
import LabAndPathology9 from "../assets/images/Lab/LabAndPathology-9.png";
import SEO from "../components/SEO";

/**
 * Lab & Pathology Designs — landing page
 * Same design system as OTSuiteDesigns.jsx — black/white/grey/ash palette,
 * spec-line cards (Zone / Size / Lab Type), 2D/3D image toggle,
 * "Get Free Quote" opens the shared Consultation modal.
 */

const FONT_IMPORT_URL =
  "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap";

const tokens = {
  ink: "#343F86",
  bg: "#ECEEF6",
  card: "#FFFFFF",
  teal: "#3A4060",
  tealDark: "#262E63",
  amber: "#8A8F9C",
  blue: "#5B6072",
  hairline: "#CDD1E0",
  sub: "#5B6072",
};

const labPathology = [
  {
    name: "Clinical Biochemistry Lab",
    zone: "Ground Floor",
    size: "35 sq.m",
    spec: "Biochemistry",
    tag: "Biochemistry",
    note: "Automated analyzer bays, sample-tracking conveyor, dedicated quality-control bench.",
    image: LabAndPathology1, // 👈 add your image path/URL here
  },
  {
    name: "Hematology & Coagulation Lab",
    zone: "Ground Floor",
    size: "30 sq.m",
    spec: "Hematology",
    tag: "Hematology",
    note: "Cell counter stations, blood-smear microscopy bench, refrigerated reagent storage.",
    image: LabAndPathology2, // 👈 add your image path/URL here
  },
  {
    name: "Microbiology & Culture Lab",
    zone: "Ground Floor",
    size: "32 sq.m",
    spec: "Microbiology",
    tag: "Microbiology",
    note: "Biosafety cabinets, incubator room, dedicated culture-plate disposal pathway.",
    image: LabAndPathology3, // 👈 add your image path/URL here
  },
  {
    name: "Histopathology & Cytology Lab",
    zone: "Ground Floor",
    size: "40 sq.m",
    spec: "Histopathology",
    tag: "Histopathology",
    note: "Tissue-processing bay, embedding and sectioning stations, ventilated grossing table.",
    image: LabAndPathology4, // 👈 add your image path/URL here
  },
  {
    name: "Molecular Diagnostics & PCR Lab",
    zone: "Ground Floor",
    size: "28 sq.m",
    spec: "Molecular / PCR",
    tag: "Molecular",
    note: "Cleanroom-grade PCR zone, sample-extraction bay, unidirectional workflow design.",
    image: LabAndPathology5, // 👈 add your image path/URL here
  },
  {
    name: "Blood Bank & Transfusion Centre",
    zone: "Ground Floor",
    size: "45 sq.m",
    spec: "Blood Bank",
    tag: "Blood Bank",
    note: "Cold-chain blood storage, donor-collection bay, dedicated cross-matching workstation.",
    image: LabAndPathology6, // 👈 add your image path/URL here
  },
  {
    name: "Central Sample Collection Centre",
    zone: "Ground Floor",
    size: "20 sq.m",
    spec: "Sample Collection",
    tag: "Collection Centre",
    note: "Phlebotomy chairs, barcoded sample-labeling station, patient privacy screening.",
    image: LabAndPathology7, // 👈 add your image path/URL here
  },
  {
    name: "Reference & Specialty Pathology Lab",
    zone: "Ground Floor",
    size: "50 sq.m",
    spec: "Specialty Testing",
    tag: "Specialty Lab",
    note: "Advanced analyzer suite, dedicated reporting room, secure sample-archival storage.",
    image: LabAndPathology8, // 👈 add your image path/URL here
  },
  {
    name: "Integrated Multi-Discipline Pathology Lab",
    zone: "Ground Floor",
    size: "120 sq.m",
    spec: "Full-Service",
    tag: "Multi-Discipline",
    note: "Combined biochemistry, hematology, microbiology and histopathology under shared workflow.",
    image: LabAndPathology9, // 👈 add your image path/URL here
  },
];

function SpecLine({ zone, size, spec }) {
  return (
    <div
      style={{
        fontFamily: "'Inter', 'Montserrat', sans-serif",
        fontSize: 11.5,
        letterSpacing: 0.3,
        color: tokens.sub,
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
      }}
    >
      <span>ZONE&nbsp;<b style={{ color: tokens.teal }}>{zone}</b></span>
      <span>·</span>
      <span>SIZE&nbsp;<b style={{ color: tokens.ink }}>{size}</b></span>
      <span>·</span>
      <span>{spec}</span>
    </div>
  );
}

function LabPathologyCard({ item, onGetQuote }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: "relative",
        background: tokens.card,
        borderRadius: 4,
        overflow: "hidden",
        border: `1px solid ${tokens.hairline}`,
        boxShadow: hover
          ? "0 14px 28px -14px rgba(14,124,123,0.35)"
          : "0 2px 6px -2px rgba(43,58,66,0.08)",
        transform: hover ? "translateY(-3px)" : "none",
        transition: "all 0.25s ease",
      }}
    >
      {/* corner bracket signature */}
      <div
        style={{
          position: "absolute", top: 10, left: 10, width: 22, height: 22,
          borderTop: `2px solid ${tokens.teal}`, borderLeft: `2px solid ${tokens.teal}`,
          zIndex: 2,
        }}
      />
      <div style={{ height: 168, position: "relative", background: tokens.tealDark }}>
        {item.image ? (
          <img
            src={item.image}
            alt={item.name}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          <div
            style={{
              width: "100%", height: "100%", display: "flex",
              alignItems: "center", justifyContent: "center",
              color: "rgba(255,255,255,0.55)",
              fontFamily: "'Inter', 'Montserrat', sans-serif", fontSize: 12, letterSpacing: 0.5,
            }}
          >
            ADD IMAGE
          </div>
        )}
        {/* tag chip */}
        <span
          style={{
            position: "absolute", bottom: 10, right: 12,
            fontFamily: "'Inter', 'Montserrat', sans-serif", fontSize: 11,
            color: "#fff", background: "rgba(0,0,0,0.45)",
            padding: "2px 8px", borderRadius: 2,
          }}
        >
          {item.tag.toUpperCase()}
        </span>
      </div>
      <div style={{ padding: "16px 16px 18px" }}>
        <h3
          style={{
            fontFamily: "'Montserrat', 'Inter', sans-serif", fontWeight: 600, fontSize: 17.5,
            color: tokens.ink, margin: "0 0 6px", lineHeight: 1.25,
          }}
        >
          {item.name}
        </h3>
        <p style={{ fontSize: 13, color: tokens.sub, margin: "0 0 10px", lineHeight: 1.45 }}>
          {item.note}
        </p>
        <SpecLine zone={item.zone} size={item.size} spec={item.spec} />
        <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
          <button
            onClick={() => onGetQuote(item)}
            style={{
              flex: 1, padding: "9px 10px", fontSize: 12.5, fontWeight: 600,
              fontFamily: "'Montserrat', 'Inter', sans-serif", letterSpacing: 0.2,
              background: tokens.amber, color: tokens.ink,
              border: "none", borderRadius: 3, cursor: "pointer",
            }}
          >
            Get Free Quote
          </button>
        </div>
      </div>
    </div>
  );
}

export default function LabPathologyDesigns() {
  const [showConsultation, setShowConsultation] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleGetQuote = (item) => {
    setSelectedItem(item);
    setShowConsultation(true);
  };

  return (
    <>
    <SEO/>
    <div
      style={{
        background: tokens.bg,
        color: tokens.ink,
        fontFamily: "'Inter', 'Montserrat', sans-serif",
        minHeight: "100vh",
      }}
    >
      <style>{`@import url('${FONT_IMPORT_URL}');
        * { box-sizing: border-box; }
        @media (prefers-reduced-motion: reduce) { * { transition: none !important; } }
        button:focus-visible, a:focus-visible { outline: 2px solid ${tokens.teal}; outline-offset: 2px; }
        .lab-grid-3x3 { grid-template-columns: repeat(3, 1fr); }
        @media (max-width: 900px) { .lab-grid-3x3 { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 600px) { .lab-grid-3x3 { grid-template-columns: 1fr; } }
        .lab-modal-overlay {
          position: fixed; inset: 0; z-index: 2000;
          background: rgba(0, 0, 0, 0.65);
          backdrop-filter: blur(5px);
          display: flex; align-items: center; justify-content: center;
          padding: 1rem;
        }
        .lab-modal-inner {
          position: relative; width: 100%; max-width: 680px;
          max-height: 90vh; overflow-y: auto; border-radius: 18px;
        }
        .lab-modal-close {
          position: absolute; top: 14px; right: 16px; z-index: 10;
          background: #262E63; border: none; color: #fff;
          width: 32px; height: 32px; border-radius: 50%;
          font-size: 20px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          line-height: 1;
        }
      `}</style>

      {/* spacer so the fixed/sticky page navbar in the host app doesn't overlap the grid */}
      <div style={{ height: 32 }} />

      {/* GRID */}
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "26px 24px 10px" }}>
        <div
          className="lab-grid-3x3"
          style={{
            display: "grid",
            gap: 22,
            marginTop: 70,
          }}
        >
          {labPathology.map((item, i) => (
            <LabPathologyCard key={i} item={item} onGetQuote={handleGetQuote} />
          ))}
        </div>
      </section>

      {/* Consultation Modal — opens on "Get Free Quote" click */}
      {showConsultation && (
        <div
          className="lab-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowConsultation(false); }}
        >
          <div className="lab-modal-inner">
            <button
              className="lab-modal-close"
              onClick={() => setShowConsultation(false)}
              aria-label="Close consultation form"
            >
              ×
            </button>
            <ConsultationForm suite={selectedItem} />
          </div>
        </div>
      )}
    </div>
    </>
  );
}